import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkle, ArrowsClockwise, PlayCircle, StopCircle, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";

/**
 * Panel admin para enriquecer el catálogo de ejercicios con
 * imágenes (Gemini Nano Banana) + videos (YouTube watch URLs)
 * y sincronizar esos datos hacia las copias en rutinas.
 *
 * Uso típico después de un deploy fresh (producción):
 *   1. "Enriquecer ejercicios" → genera imágenes y resuelve watch URLs
 *   2. "Sincronizar rutinas" → propaga a las copias en rutinas
 */
export default function EnrichmentPanel() {
  const [status, setStatus] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [startBusy, setStartBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const pollingRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/exercises/enrich/status");
      setStatus(data);
      return data;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    fetchStatus();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const startPolling = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      const s = await fetchStatus();
      if (s && !s.running) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }, 5000);
  };

  const runEnrichment = async (withImages, forceUpgrade) => {
    setStartBusy(true);
    try {
      const qs = `?with_images=${withImages}&force_video_upgrade=${forceUpgrade}`;
      const { data } = await api.post(`/exercises/enrich${qs}`);
      if (data.already_running) {
        toast.info("Ya hay un job corriendo");
      } else {
        toast.success("Enriquecimiento iniciado");
      }
      setStatus(data);
      startPolling();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setStartBusy(false);
    }
  };

  const runSync = async (overwrite) => {
    setSyncBusy(true);
    try {
      const { data } = await api.post(`/routines/sync-media?overwrite=${overwrite}`);
      toast.success(`${data.routines_updated} rutina${data.routines_updated === 1 ? "" : "s"} · ${data.exercises_updated} ejercicio${data.exercises_updated === 1 ? "" : "s"} actualizados`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncBusy(false);
    }
  };

  const runSimplyFitnessImport = async () => {
    setImportBusy(true);
    try {
      const { data } = await api.post("/exercises/import-simplyfitness");
      setImportResult(data);
      if (data.inserted > 0) {
        toast.success(`${data.inserted} ejercicios nuevos agregados · Total: ${data.total_after}`);
      } else {
        toast.info(`Sin ejercicios nuevos. Ya estaban los ${data.skipped_duplicates} del catálogo.`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImportBusy(false);
    }
  };

  const running = !!status?.running;
  const finished = status?.finished_at && !running;

  return (
    <div className="card-base p-5" data-testid="enrichment-panel">
      <div className="flex items-center gap-2 mb-4">
        <Sparkle size={20} weight="duotone" className="text-primary-app" />
        <h2 className="font-display font-black uppercase text-lg tracking-tight">Enriquecimiento del catálogo</h2>
      </div>
      <p className="text-xs text-muted leading-relaxed mb-4">
        Genera imágenes (Gemini Nano Banana) y resuelve URLs de YouTube para todos los ejercicios que no las tengan.
        Después, propaga esos datos a las copias que viven dentro de rutinas.
      </p>

      {/* Status */}
      {status && (
        <div className="bg-[#0A0A0A] border border-app p-3 mb-4 text-xs" data-testid="enrichment-status">
          <div className="flex items-center justify-between mb-2">
            <span className="uppercase tracking-widest text-muted">Estado</span>
            {running ? (
              <span className="flex items-center gap-1 text-[#FFD60A] font-bold">
                <ArrowsClockwise size={12} weight="bold" className="animate-spin" /> Corriendo
              </span>
            ) : finished ? (
              <span className="flex items-center gap-1 text-[#32D74B] font-bold">
                <CheckCircle size={12} weight="fill" /> Completado
              </span>
            ) : (
              <span className="flex items-center gap-1 text-muted">
                <StopCircle size={12} weight="bold" /> Idle
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div>Total: <b>{status.total || 0}</b></div>
            <div>Fallos: <b>{status.failures || 0}</b></div>
            <div>Videos nuevos: <b>{status.processed_video || 0}</b></div>
            <div>Imágenes nuevas: <b>{status.processed_image || 0}</b></div>
            <div className="col-span-2">Upgrades search→watch: <b>{status.upgraded_video || 0}</b></div>
          </div>
          {status.last_error && (
            <div className="mt-2 text-[#FF3B30]">Error: {status.last_error}</div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          onClick={() => runEnrichment(true, true)}
          disabled={running || startBusy}
          className="btn-primary text-xs !py-2 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="enrichment-run-full-btn"
          title="Corre todo: genera imágenes faltantes + resuelve watch URLs + upgradea las search URLs viejas"
        >
          <PlayCircle size={14} weight="fill" /> Enriquecer ejercicios
        </button>
        <button
          onClick={() => runSync(true)}
          disabled={syncBusy}
          className="btn-secondary text-xs !py-2 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="enrichment-sync-btn"
          title="Copia los video_url/image_url del catálogo maestro a todas las rutinas creadas antes del enriquecimiento"
        >
          <ArrowsClockwise size={14} weight="bold" /> Sincronizar rutinas
        </button>
      </div>
      <div className="text-[10px] text-muted mt-2 leading-relaxed">
        Flujo recomendado: 1) Enriquecer ejercicios (tarda ~1-2h con imágenes). 2) Cuando el estado muestre &quot;Completado&quot;, sincronizar rutinas.
      </div>

      {/* Import Simply Fitness catalog (poblar producción) */}
      <div className="border-t border-app mt-4 pt-4">
        <div className="text-xs uppercase tracking-widest text-muted mb-2">Importar catálogo Simply Fitness</div>
        <p className="text-[11px] text-muted leading-relaxed mb-3">
          Agrega 139 ejercicios en español (por músculo, con URL fuente). Idempotente: si ya existen, los saltea.
          Usalo una única vez en producción después de cada deploy que traiga catálogo nuevo.
        </p>
        <button
          onClick={runSimplyFitnessImport}
          disabled={importBusy}
          className="btn-secondary text-xs !py-2 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed w-full"
          data-testid="import-simplyfitness-btn"
        >
          <Sparkle size={14} weight="fill" />
          {importBusy ? "Importando..." : "Importar 139 ejercicios"}
        </button>
        {importResult && (
          <div className="mt-3 bg-[#0A0A0A] border border-app p-3 text-[11px]" data-testid="import-result">
            <div>Ya estaban: <b>{importResult.skipped_duplicates}</b></div>
            <div>Nuevos: <b className="text-[#32D74B]">{importResult.inserted}</b></div>
            <div>Total en catálogo: <b>{importResult.total_after}</b></div>
          </div>
        )}
      </div>
    </div>
  );
}
